import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Modal, useToast } from '../../components/primitives';
import {
  DocLayout,
  DocPage,
  DocBlock,
  DocRow,
  DocActionButton,
  DocInlineDate,
  DocContactQR,
  DocInlineAresName,
  DocInlineAresLookup,
  useDocRows,
  DocCustomRows,
  DocAddRowButton,
  type DocRows,
  type DocRowsSnapshot,
} from '../../components/document';
import { SignaturePad } from '../../components/SignaturePad';
import { MapPickerButton } from '../../components/map';
import {
  CompanyPickerButton,
  SaveCompanyButton,
  type AresPickResult,
  type CompanyPickResult,
} from '../../components/ares';
import {
  StavbaPickerButton,
  SaveStavbaButton,
  type StavbaPickResult,
} from '../../components/stavba';
import { SendEmailModal } from '../../components/SendEmailModal';
import { buildDocHtml } from '../../lib/docHtml';
import { printToPdf } from '../../lib/printToPdf';
import { docSaveErrorMessage } from '../../lib/docSaveError';
import { useActiveStavba } from '../stavba-context/StavbaContext';
import {
  useKontrolniDen,
  useUpdateKontrolniDen,
  useKontrolniDny,
  useKdNovyZPredchoziho,
  useKdExtractVady,
  useKdNotify,
  useKdNesplnene,
  useKdAttachments,
  useKdUploadAttachment,
  useKdDeleteAttachment,
  useKdPhotos,
  useKdLinkPhotos,
  usePhotos,
  useTemplates,
  useCreateTemplate,
  useDeleteTemplate,
} from '../../api/hooks';
import { getAuthToken } from '../../lib/api';

/**
 * KontrolniDenEditor — "živý dokument" editor jednoho kontrolního dne.
 *
 * Sekce kopírují strukturu z původního HTML formuláře (doc-zapis-kd):
 *   01 Základní identifikace
 *   02 Účastníci (Objednatel / TDS / Zhotovitel / Ostatní)
 *   03 Stav prací na stavbě
 *   04 Harmonogram (kontrola termínů)
 *   05 Body programu / projednávané věci
 *   06 Vady a nedodělky
 *   07 Přílohy
 *   08 Rozeslání
 *
 * Vše se ukládá do data_json přes PUT /stavby/{sid}/kd/{kd_id}.
 */

interface Ucastnik {
  id: string;
  jmeno: string;
  telefon: string;
  email: string;
  pritomen: boolean;
  podpis?: string;  // data URL podpisu (PNG)
}

interface UcastGroup {
  id: string;
  nazev: string;        // label sekce, např. "Objednatel"
  subjekt: string;      // firma / osoba, např. "Stavby Novák s.r.o."
  ico?: string;
  adresa?: string;
  ucastnici: Ucastnik[];
}

interface HarmonogramRow {
  id: string;
  polozka: string;
  plan: string;
  skutecnost: string;
}

interface ProgramRow {
  id: string;
  popis: string;
  odpovedny: string;
  termin: string;
}

interface VadaRow {
  id: string;
  popis: string;
  misto: string;
  termin: string;
}

export interface KdData {
  cislo?: string;         // uložené v KD.cislo, pro zobrazení KD-{číslo}
  datum?: string;         // dd. mm. rrrr
  smlouva?: string;
  misto?: string;
  casZahajeni?: string;
  terminDokonceni?: string;
  firma?: string;
  stavba?: string;
  ucast: UcastGroup[];
  stavPraci?: string;
  harmonogram: HarmonogramRow[];
  program: ProgramRow[];
  vady: VadaRow[];
  prilohy?: string;
  rozeslani?: string;
  /** Skryté standardní řádky + vlastní řádky v sekci 01 (viz useDocRows). */
  radky?: DocRowsSnapshot;
}

const DEFAULT_GROUPS: UcastGroup[] = [
  { id: 'obj', nazev: 'Objednatel', subjekt: '', ucastnici: [{ id: 'obj-1', jmeno: '', telefon: '', email: '', pritomen: false }] },
  { id: 'tds', nazev: 'Technický dozor stavebníka (TDS)', subjekt: '', ucastnici: [{ id: 'tds-1', jmeno: '', telefon: '', email: '', pritomen: false }] },
  { id: 'zhot', nazev: 'Zhotovitel', subjekt: '', ucastnici: [{ id: 'zhot-1', jmeno: '', telefon: '', email: '', pritomen: false }] },
  { id: 'host', nazev: 'Ostatní / hosté', subjekt: '', ucastnici: [{ id: 'host-1', jmeno: '', telefon: '', email: '', pritomen: false }] },
];

function newId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

function emptyData(): KdData {
  return {
    ucast: DEFAULT_GROUPS,
    harmonogram: [],
    program: [],
    vady: [],
    rozeslani: 'Zápis byl rozeslán: Objednatel, TDS, Zhotovitel',
  };
}

export function mergeWithDefaults(raw: unknown): KdData {
  const base = emptyData();
  if (!raw || typeof raw !== 'object') return base;
  const d = raw as Partial<KdData>;
  return {
    cislo: d.cislo ?? base.cislo,
    datum: d.datum ?? base.datum,
    smlouva: d.smlouva ?? base.smlouva,
    misto: d.misto ?? base.misto,
    casZahajeni: d.casZahajeni ?? base.casZahajeni,
    terminDokonceni: d.terminDokonceni ?? base.terminDokonceni,
    firma: d.firma ?? base.firma,
    stavba: d.stavba ?? base.stavba,
    ucast: Array.isArray(d.ucast) && d.ucast.length > 0 ? d.ucast : base.ucast,
    stavPraci: d.stavPraci ?? base.stavPraci,
    harmonogram: Array.isArray(d.harmonogram) ? d.harmonogram : base.harmonogram,
    program: Array.isArray(d.program) ? d.program : base.program,
    vady: Array.isArray(d.vady) ? d.vady : base.vady,
    prilohy: d.prilohy ?? base.prilohy,
    rozeslani: d.rozeslani ?? base.rozeslani,
    radky: d.radky && typeof d.radky === 'object' ? d.radky : undefined,
  };
}

export function czDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString('cs-CZ');
}

export function KontrolniDenEditorPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { kdId: kdIdParam } = useParams<{ kdId: string }>();
  const kdIdNum = kdIdParam ? Number(kdIdParam) : null;
  const { activeStavba } = useActiveStavba();

  const { data: kd, isLoading, error } = useKontrolniDen(activeStavba?.id ?? null, kdIdNum);
  const update = useUpdateKontrolniDen(activeStavba?.id ?? null);
  const novyZPred = useKdNovyZPredchoziho(activeStavba?.id ?? null);
  const extractVady = useKdExtractVady(activeStavba?.id ?? null);
  const notify = useKdNotify(activeStavba?.id ?? null);
  const { data: nesplnene = [] } = useKdNesplnene(activeStavba?.id ?? null);
  const { data: attachments = [] } = useKdAttachments(activeStavba?.id ?? null, kdIdNum);
  const uploadAtt = useKdUploadAttachment(activeStavba?.id ?? null, kdIdNum);
  const deleteAtt = useKdDeleteAttachment(activeStavba?.id ?? null, kdIdNum);
  const { data: kdPhotos = [] } = useKdPhotos(activeStavba?.id ?? null, kdIdNum);
  const linkPhotos = useKdLinkPhotos(activeStavba?.id ?? null, kdIdNum);
  const { data: allPhotos = [] } = usePhotos(activeStavba?.id ?? null);
  const { data: allKd = [] } = useKontrolniDny(activeStavba?.id ?? null);
  const { data: templates = [] } = useTemplates('zapis-kd');
  const createTpl = useCreateTemplate();
  const deleteTpl = useDeleteTemplate();

  const [data, setData] = useState<KdData>(emptyData);
  const [dirty, setDirty] = useState(false);
  // Skryté a vlastní řádky sekce 01. Každá změna musí označit zápis jako
  // neuložený, jinak by tlačítko „Uložit" zůstalo zamčené (gate `dirty`).
  const baseRows = useDocRows();
  const docRows: DocRows = useMemo(
    () => ({
      ...baseRows,
      hide: (k) => { baseRows.hide(k); setDirty(true); },
      restoreSection: (s) => { baseRows.restoreSection(s); setDirty(true); },
      addCustomRow: (s) => { baseRows.addCustomRow(s); setDirty(true); },
      updateCustomRow: (s, id, p) => { baseRows.updateCustomRow(s, id, p); setDirty(true); },
      removeCustomRow: (s, id) => { baseRows.removeCustomRow(s, id); setDirty(true); },
    }),
    [baseRows],
  );
  const [emailOpen, setEmailOpen] = useState(false);
  const [photoPickerOpen, setPhotoPickerOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [tplNewName, setTplNewName] = useState('');
  const [pickedPhotoIds, setPickedPhotoIds] = useState<Set<number>>(new Set());
  const [extractResult, setExtractResult] = useState<string | null>(null);
  const [notifyResult, setNotifyResult] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const previewRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!kd) return;
    const merged = mergeWithDefaults(kd.data_json);
    if (!merged.datum) merged.datum = czDate(kd.datum);
    if (!merged.firma) merged.firma = activeStavba?.nazev ? '' : '';
    if (!merged.stavba && activeStavba?.nazev) merged.stavba = activeStavba.nazev;
    setData(merged);
    baseRows.apply(merged.radky);
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kd, activeStavba?.nazev]);

  const patch = (p: Partial<KdData>) => {
    setData((d) => ({ ...d, ...p }));
    setDirty(true);
  };

  async function handleSave() {
    if (!kdIdNum) return;
    try {
      await update.mutateAsync({
        kdId: kdIdNum,
        data_json: { ...data, radky: baseRows.snapshot() } as unknown as Record<string, unknown>,
      });
      setDirty(false);
      toast.success('Zápis z kontrolního dne uložen.');
    } catch (e) {
      // Dřív se chyba tiše ztratila — tlačítko zůstalo na „Uložit" a uživatel
      // netušil, že zápis v databázi nepřistál.
      toast.error(docSaveErrorMessage(e));
    }
  }

  async function handleCarryOver() {
    if (!kdIdNum) return;
    const ok = window.confirm(
      'Vytvoří se nový KD a převezmou se do něj nesplněné úkoly z tohoto KD. Pokračovat?',
    );
    if (!ok) return;
    const nextKd = await novyZPred.mutateAsync(kdIdNum);
    navigate(`/app/kontrolni-dny/${nextKd.id}`);
  }

  async function handleExtractVady() {
    if (!kdIdNum) return;
    try {
      const res = await extractVady.mutateAsync(kdIdNum);
      setExtractResult(
        res.created > 0
          ? `Přeneseno ${res.created} vad do evidence.`
          : 'Žádné nové vady k přenesení.',
      );
    } catch (e) {
      setExtractResult(`Chyba: ${e instanceof Error ? e.message : 'neznámá'}`);
    }
  }

  async function handleNotify() {
    if (!kdIdNum) return;
    try {
      const res = await notify.mutateAsync(kdIdNum);
      if (res.message) {
        setNotifyResult(res.message);
      } else {
        const n = res.notified?.length ?? 0;
        const skipped = res.skipped?.length ?? 0;
        setNotifyResult(
          `Odeslán email: ${n} příjemců` + (skipped > 0 ? ` · ${skipped} bez emailu` : ''),
        );
      }
    } catch (e) {
      setNotifyResult(`Chyba notifikace: ${e instanceof Error ? e.message : 'neznámá'}`);
    }
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (!files || !kdIdNum) return;
    for (const file of Array.from(files)) {
      await uploadAtt.mutateAsync(file);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function handleSaveTemplate() {
    const name = tplNewName.trim();
    if (!name) return;
    // Ukládáme jen rozumná „šablonová" pole — ne fotky/přílohy, ne datum,
    // ale zachováváme strukturu účastníků, harmonogram, program, přílohy seznam.
    const tplData = {
      smlouva: data.smlouva,
      terminDokonceni: data.terminDokonceni,
      ucast: data.ucast.map((g) => ({
        ...g,
        ucastnici: g.ucastnici.map((p) => ({ ...p, podpis: '' })),  // podpisy neukládáme
      })),
      stavPraci: data.stavPraci,
      harmonogram: data.harmonogram,
      program: data.program,
      prilohy: data.prilohy,
      rozeslani: data.rozeslani,
      radky: baseRows.snapshot(),
    };
    await createTpl.mutateAsync({ doc_type: 'zapis-kd', name, data_json: tplData });
    setTplNewName('');
  }

  function handleLoadTemplate(tpl: { data_json: string; name: string }) {
    try {
      const raw = typeof tpl.data_json === 'string' ? JSON.parse(tpl.data_json) : tpl.data_json;
      const merged = mergeWithDefaults(raw);
      // Zachovat aktuální číslo/datum z právě otevřeného KD
      merged.cislo = data.cislo;
      merged.datum = data.datum;
      merged.stavba = data.stavba;
      merged.firma = data.firma;
      setData(merged);
      baseRows.apply(merged.radky ?? { hidden: [], custom: {} });
      setDirty(true);
      setTemplatesOpen(false);
    } catch (e) {
      window.alert(`Šablonu se nepodařilo načíst: ${e instanceof Error ? e.message : 'chyba JSON'}`);
    }
  }

  async function handleLinkPickedPhotos() {
    if (!kdIdNum || pickedPhotoIds.size === 0) return;
    await linkPhotos.mutateAsync([...pickedPhotoIds]);
    setPickedPhotoIds(new Set());
    setPhotoPickerOpen(false);
  }

  function togglePick(id: number) {
    setPickedPhotoIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // ─── ÚČASTNÍCI ─────────────────────────────────────────────────────────
  const updGroup = (gid: string, changes: Partial<UcastGroup>) =>
    patch({ ucast: data.ucast.map((g) => (g.id === gid ? { ...g, ...changes } : g)) });

  const addGroup = () =>
    patch({
      ucast: [
        ...data.ucast,
        { id: newId('g'), nazev: 'Nová skupina', subjekt: '', ucastnici: [{ id: newId('p'), jmeno: '', telefon: '', email: '', pritomen: false }] },
      ],
    });

  const removeGroup = (gid: string) =>
    patch({ ucast: data.ucast.filter((g) => g.id !== gid) });

  const addRow = (gid: string) =>
    updGroup(gid, {
      ucastnici: [
        ...(data.ucast.find((g) => g.id === gid)?.ucastnici ?? []),
        { id: newId('p'), jmeno: '', telefon: '', email: '', pritomen: false },
      ],
    });

  const updRow = (gid: string, rid: string, changes: Partial<Ucastnik>) => {
    const g = data.ucast.find((x) => x.id === gid);
    if (!g) return;
    updGroup(gid, {
      ucastnici: g.ucastnici.map((r) => (r.id === rid ? { ...r, ...changes } : r)),
    });
  };

  const removeRow = (gid: string, rid: string) => {
    const g = data.ucast.find((x) => x.id === gid);
    if (!g) return;
    updGroup(gid, { ucastnici: g.ucastnici.filter((r) => r.id !== rid) });
  };

  // ─── HARMONOGRAM ───────────────────────────────────────────────────────
  const addHarm = () => patch({ harmonogram: [...data.harmonogram, { id: newId('h'), polozka: '', plan: '', skutecnost: '' }] });
  const updHarm = (id: string, ch: Partial<HarmonogramRow>) =>
    patch({ harmonogram: data.harmonogram.map((r) => (r.id === id ? { ...r, ...ch } : r)) });
  const rmHarm = (id: string) => patch({ harmonogram: data.harmonogram.filter((r) => r.id !== id) });

  // ─── PROGRAM ───────────────────────────────────────────────────────────
  const addProg = () => patch({ program: [...data.program, { id: newId('pr'), popis: '', odpovedny: '', termin: '' }] });
  const updProg = (id: string, ch: Partial<ProgramRow>) =>
    patch({ program: data.program.map((r) => (r.id === id ? { ...r, ...ch } : r)) });
  const rmProg = (id: string) => patch({ program: data.program.filter((r) => r.id !== id) });

  // ─── VADY ──────────────────────────────────────────────────────────────
  const addVada = () => patch({ vady: [...data.vady, { id: newId('v'), popis: '', misto: '', termin: '' }] });
  const updVada = (id: string, ch: Partial<VadaRow>) =>
    patch({ vady: data.vady.map((r) => (r.id === id ? { ...r, ...ch } : r)) });
  const rmVada = (id: string) => patch({ vady: data.vady.filter((r) => r.id !== id) });

  const cisloLabel = useMemo(
    () => (kd ? `KD-${String(kd.cislo).padStart(2, '0')}` : '—'),
    [kd],
  );

  if (!activeStavba) {
    return (
      <DocLayout breadcrumb={['Realizace', 'Kontrolní den']} title="Kontrolní den" statusText="Vyberte stavbu" status="draft">
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--sd-doc-dim)' }}>
          Nejdřív vyberte stavbu v sidebaru.
        </div>
      </DocLayout>
    );
  }

  if (isLoading) {
    return (
      <DocLayout breadcrumb={['Realizace', activeStavba.nazev, 'Kontrolní den']} title="Načítám…" statusText="Načítám" status="draft">
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--sd-doc-dim)' }}>Načítám…</div>
      </DocLayout>
    );
  }

  if (error || !kd) {
    return (
      <DocLayout breadcrumb={['Realizace', activeStavba.nazev, 'Kontrolní den']} title="Chyba" statusText="Chyba" status="draft">
        <div style={{ padding: 24, background: 'var(--sd-danger-soft)', color: 'var(--sd-danger)', borderRadius: 4 }}>
          KD se nepodařilo načíst: {error instanceof Error ? error.message : 'neznámá chyba'}
        </div>
      </DocLayout>
    );
  }

  const status: 'draft' | 'done' = kd.stav === 'uzavreno' ? 'done' : 'draft';
  const statusText = kd.stav === 'uzavreno' ? 'Uzavřen' : dirty ? 'Neuloženo' : 'Rozpracovaný koncept';

  return (
    <DocLayout
      breadcrumb={['Realizace', activeStavba.nazev, 'Kontrolní dny', cisloLabel]}
      title={`Zápis z kontrolního dne — ${cisloLabel}`}
      status={status}
      statusText={statusText}
      actions={
        <>
          <Button variant="secondary" onClick={() => navigate('/app/kontrolni-dny')}>← Seznam</Button>
          <Button
            variant="secondary"
            onClick={() => setHistoryOpen(true)}
            title="Seznam všech KD této stavby"
          >
            📋 Historie
          </Button>
          <Button
            variant="secondary"
            onClick={() => setTemplatesOpen(true)}
            title="Načíst nebo uložit šablonu"
          >
            🗂 Šablony
          </Button>
          <Button
            variant="secondary"
            onClick={handleCarryOver}
            disabled={novyZPred.isPending}
            title="Vytvořit nový KD s převzetím nesplněných úkolů"
          >
            {novyZPred.isPending ? 'Zakládám…' : '➡️ Nový z předchozího'}
          </Button>
          <Button
            variant="secondary"
            onClick={handleExtractVady}
            disabled={extractVady.isPending}
            title="Přenést vady z tohoto KD do evidence vad"
          >
            {extractVady.isPending ? 'Přenáším…' : '🔴 Vady do evidence'}
          </Button>
          <Button
            variant="secondary"
            onClick={handleNotify}
            disabled={notify.isPending}
            title="Odeslat email s nesplněnými úkoly zodpovědným osobám"
          >
            {notify.isPending ? 'Odesílám…' : '📧 Notifikace'}
          </Button>
          <Button variant="secondary" leading="📧" onClick={() => setEmailOpen(true)}>
            Odeslat e-mailem
          </Button>
          <Button variant="secondary" onClick={() => printToPdf('Kontrolni_den.pdf')} leading="⎙">Tisk</Button>
          <Button variant="primary" onClick={handleSave} disabled={update.isPending || !dirty}>
            {update.isPending ? 'Ukládám…' : update.isSuccess && !dirty ? 'Uloženo ✓' : 'Uložit'}
          </Button>
        </>
      }
    >
      {(nesplnene.length > 0 || extractResult || notifyResult) && (
        <div className="docNoPrint" style={{ marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {nesplnene.length > 0 && (
            <div style={{ background: '#fff3e0', border: '1.5px solid #f0941f', borderRadius: 4, padding: '10px 14px' }}>
              <div style={{ fontWeight: 800, color: '#b45309', fontSize: 13, marginBottom: 6 }}>
                ⚠️ Nesplněné úkoly z minulých KD ({nesplnene.length})
              </div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#6a3b0a', lineHeight: 1.6 }}>
                {nesplnene.slice(0, 8).map((u) => (
                  <li key={u.id}>
                    <span style={{ fontFamily: 'var(--sd-font-mono)', fontWeight: 700 }}>KD-{u.kd_id}</span>{' '}
                    — {u.popis}
                    {u.termin && <span style={{ color: '#975a10', marginLeft: 6 }}>(termín {u.termin})</span>}
                  </li>
                ))}
                {nesplnene.length > 8 && <li style={{ fontStyle: 'italic' }}>… + {nesplnene.length - 8} dalších</li>}
              </ul>
            </div>
          )}
          {extractResult && (
            <div style={{ background: 'var(--sd-success-soft)', border: '1.5px solid rgba(52,211,153,0.4)', borderRadius: 4, padding: '8px 14px', fontSize: 13, color: 'var(--sd-success)' }}>
              {extractResult} <button onClick={() => setExtractResult(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', float: 'right' }}>✕</button>
            </div>
          )}
          {notifyResult && (
            <div style={{ background: 'var(--sd-success-soft)', border: '1.5px solid rgba(52,211,153,0.4)', borderRadius: 4, padding: '8px 14px', fontSize: 13, color: 'var(--sd-success)' }}>
              {notifyResult} <button onClick={() => setNotifyResult(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', float: 'right' }}>✕</button>
            </div>
          )}
        </div>
      )}

      <div ref={previewRef}>
      <DocPage
        company={{
          initials: (data.firma || activeStavba.nazev || '—').split(/\s+/).filter(Boolean).slice(0, 3).map((w) => w[0]?.toUpperCase() ?? '').join('') || '—',
          name: data.firma || '—',
          address: data.stavba || activeStavba.nazev,
        }}
        tag="Kontrolní den"
        title="Zápis z kontrolního dne"
        meta={`č. ${cisloLabel} · ${data.datum || czDate(kd.datum) || '—'}`}
        footer={{ left: data.firma || '—', center: `str. 1 / 1`, right: cisloLabel }}
      >
        {/* 01 — Základní identifikace */}
        <DocBlock
          idx="01"
          title="Základní identifikace"
          hiddenCount={docRows.hiddenCount('s01')}
          onRestoreHidden={() => docRows.restoreSection('s01')}
          actions={
            <>
              <DocAddRowButton rows={docRows} section="s01" />
              <StavbaPickerButton
                title="Načíst stavbu z adresáře"
                onPick={(s: StavbaPickResult) =>
                  patch({
                    stavba: s.nazev,
                    misto: s.adresa,
                    terminDokonceni: s.datum_ukonceni || data.terminDokonceni,
                    smlouva: data.smlouva,
                  })
                }
              />
              <SaveStavbaButton
                title="Uložit stavbu do adresáře"
                build={() => ({
                  nazev: data.stavba || activeStavba.nazev,
                  adresa: data.misto || '',
                  stavebni_povoleni: '',
                  zakazka_cislo: activeStavba.zakazka_cislo || '',
                  datum_ukonceni: data.terminDokonceni || '',
                })}
              />
            </>
          }
        >
          {!docRows.isHidden('s01.cislo') && (
            <DocRow k="Číslo zápisu" mono v={cisloLabel} onRemove={() => docRows.hide('s01.cislo')} />
          )}
          {!docRows.isHidden('s01.datum') && (
            <DocRow
              k="Datum"
              mono
              value={data.datum ?? ''}
              onChange={(v) => patch({ datum: v })}
              placeholder="dd. mm. rrrr"
              actions={<DocInlineDate value={data.datum ?? ''} onChange={(v) => patch({ datum: v })} />}
              onRemove={() => docRows.hide('s01.datum')}
            />
          )}
          {!docRows.isHidden('s01.smlouva') && (
            <DocRow
              k="Smlouva o dílo"
              value={data.smlouva ?? ''}
              onChange={(v) => patch({ smlouva: v })}
              placeholder="č. SOD-2026/0412 ze dne …"
              onRemove={() => docRows.hide('s01.smlouva')}
            />
          )}
          {!docRows.isHidden('s01.misto') && (
            <DocRow
              k="Místo konání"
              value={data.misto ?? ''}
              onChange={(v) => patch({ misto: v })}
              actions={
                <MapPickerButton
                  value={data.misto ?? ''}
                  onApply={(v) => patch({ misto: v })}
                  title="Místo konání KD"
                  label="Mapa"
                />
              }
              onRemove={() => docRows.hide('s01.misto')}
            />
          )}
          {!docRows.isHidden('s01.cas') && (
            <DocRow
              k="Čas zahájení"
              mono
              value={data.casZahajeni ?? ''}
              onChange={(v) => patch({ casZahajeni: v })}
              placeholder="10:00"
              onRemove={() => docRows.hide('s01.cas')}
            />
          )}
          {!docRows.isHidden('s01.termin') && (
            <DocRow
              k="Termín dokončení díla"
              mono
              value={data.terminDokonceni ?? ''}
              onChange={(v) => patch({ terminDokonceni: v })}
              placeholder="dd. mm. rrrr"
              actions={<DocInlineDate value={data.terminDokonceni ?? ''} onChange={(v) => patch({ terminDokonceni: v })} />}
              onRemove={() => docRows.hide('s01.termin')}
            />
          )}
          <DocCustomRows rows={docRows} section="s01" />
        </DocBlock>

        {/* 02 — Účastníci */}
        <DocBlock
          idx="02"
          title="Účastníci"
          actions={<DocActionButton onClick={addGroup} title="Přidat skupinu účastníků">+ Skupina</DocActionButton>}
        >
          {data.ucast.map((g) => {
            // Mapa typu skupiny na roli v adresáři firem.
            const nlow = g.nazev.toLowerCase();
            const preferRole: 'subdodavatel' | 'investitor' | 'projektant' | undefined =
              nlow.startsWith('objednatel') || nlow.startsWith('investor')
                ? 'investitor'
                : nlow.startsWith('zhotovitel') || nlow.startsWith('dodavatel')
                  ? 'subdodavatel'
                  : nlow.startsWith('tds') || nlow.includes('dozor')
                    ? 'projektant'
                    : undefined;
            const firstP = g.ucastnici[0];
            return (
              <Fragment key={g.id}>
                <DocRow
                  k={g.nazev}
                  v={
                    <DocInlineAresName
                      value={g.subjekt}
                      onChange={(v) => updGroup(g.id, { subjekt: v })}
                      onPick={(d: AresPickResult) =>
                        updGroup(g.id, { subjekt: d.nazev, ico: d.ico, adresa: d.adresa })
                      }
                    />
                  }
                  actions={
                    <>
                      <CompanyPickerButton
                        preferRole={preferRole}
                        title={`Načíst ${g.nazev} z adresáře`}
                        onPick={(c: CompanyPickResult) => {
                          updGroup(g.id, {
                            subjekt: c.nazev,
                            ico: c.ico,
                            adresa: c.adresa,
                          });
                          // Naplnit první řádek účastníků kontaktem z firmy
                          const first = g.ucastnici[0];
                          if (first && !first.jmeno) {
                            updRow(g.id, first.id, {
                              jmeno: c.kontakt_jmeno || c.stavbyvedouci || '',
                              telefon: c.kontakt_tel || '',
                              email: c.kontakt_email || '',
                            });
                          }
                        }}
                      />
                      <SaveCompanyButton
                        title={`Uložit ${g.nazev} do adresáře`}
                        build={() => ({
                          role: preferRole ?? 'subdodavatel',
                          nazev: g.subjekt || g.nazev,
                          ico: g.ico || '',
                          adresa: g.adresa || '',
                          kontakt_jmeno: firstP?.jmeno || '',
                          kontakt_tel: firstP?.telefon || '',
                          kontakt_email: firstP?.email || '',
                        })}
                      />
                      <DocActionButton onClick={() => addRow(g.id)} title="Přidat účastníka">+ Osoba</DocActionButton>
                      <DocActionButton onClick={() => removeGroup(g.id)} title="Odebrat skupinu">✕</DocActionButton>
                    </>
                  }
                />
                <DocRow
                  k="IČO"
                  mono
                  value={g.ico ?? ''}
                  onChange={(v) => updGroup(g.id, { ico: v })}
                  actions={
                    <DocInlineAresLookup
                      ico={g.ico ?? ''}
                      onResult={(d) =>
                        updGroup(g.id, { subjekt: d.nazev, ico: d.ico, adresa: d.adresa })
                      }
                    />
                  }
                />
                <DocRow
                  k="Adresa"
                  value={g.adresa ?? ''}
                  onChange={(v) => updGroup(g.id, { adresa: v })}
                  actions={
                    <MapPickerButton
                      value={g.adresa ?? ''}
                      onApply={(v) => updGroup(g.id, { adresa: v })}
                      title={`Adresa — ${g.nazev}`}
                      label="Mapa"
                    />
                  }
                />
                {g.ucastnici.map((p, i) => (
                  <Fragment key={p.id}>
                    <DocRow
                      k={`${g.nazev} — ${i + 1}. jméno`}
                      value={p.jmeno}
                      onChange={(v) => updRow(g.id, p.id, { jmeno: v })}
                      placeholder="Jméno a funkce"
                      actions={
                        <>
                          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--sd-doc-dim)', fontWeight: 600 }}>
                            <input
                              type="checkbox"
                              checked={p.pritomen}
                              onChange={(e) => updRow(g.id, p.id, { pritomen: e.target.checked })}
                            />
                            Přítomen
                          </label>
                          <CompanyPickerButton
                            preferRole={preferRole}
                            title={`Načíst kontakt — ${g.nazev}`}
                            onPick={(c: CompanyPickResult) =>
                              updRow(g.id, p.id, {
                                jmeno: c.kontakt_jmeno || c.stavbyvedouci || c.nazev,
                                telefon: c.kontakt_tel || '',
                                email: c.kontakt_email || '',
                              })
                            }
                          />
                          <DocActionButton onClick={() => removeRow(g.id, p.id)} title="Odebrat řádek">✕</DocActionButton>
                        </>
                      }
                    />
                    <DocRow
                      k="Telefon"
                      mono
                      value={p.telefon}
                      onChange={(v) => updRow(g.id, p.id, { telefon: v })}
                      inputType="tel"
                    />
                    <DocRow
                      k="E-mail"
                      mono
                      value={p.email}
                      onChange={(v) => updRow(g.id, p.id, { email: v })}
                      inputType="email"
                      actions={
                        <DocContactQR
                          name={p.jmeno}
                          role={g.nazev}
                          org={g.subjekt}
                          phone={p.telefon}
                          email={p.email}
                          compact
                          label="QR"
                          title={`QR vizitka — ${p.jmeno || 'účastník'}`}
                        />
                      }
                    />
                    <DocRow
                      k="Podpis"
                      v={
                        <div style={{ width: '100%', maxWidth: 420 }}>
                          <SignaturePad
                            value={p.podpis ?? ''}
                            onChange={(dataUrl) => updRow(g.id, p.id, { podpis: dataUrl })}
                            width={400}
                            height={110}
                            hint="Podepsat prstem / myší"
                          />
                        </div>
                      }
                    />
                  </Fragment>
                ))}
              </Fragment>
            );
          })}
        </DocBlock>

        {/* 03 — Stav prací */}
        <DocBlock idx="03" title="Stav prací na stavbě">
          <DocRow
            k="Popis"
            multiline
            value={data.stavPraci ?? ''}
            onChange={(v) => patch({ stavPraci: v })}
            placeholder="Aktuální stav, probíhající činnosti, dokončené etapy…"
          />
        </DocBlock>

        {/* 04 — Harmonogram */}
        <DocBlock
          idx="04"
          title="Kontrola termínů a harmonogramu"
          actions={<DocActionButton onClick={addHarm} title="Přidat řádek">+ Řádek</DocActionButton>}
        >
          {data.harmonogram.length === 0 && (
            <DocRow k="—" v={<span style={{ color: 'var(--sd-doc-dim)' }}>Zatím žádný záznam. Použij „+ Řádek".</span>} />
          )}
          {data.harmonogram.map((r) => (
            <Fragment key={r.id}>
              <DocRow
                k="Položka / etapa"
                value={r.polozka}
                onChange={(v) => updHarm(r.id, { polozka: v })}
                placeholder="Hrubá stavba, vnitřní omítky…"
                actions={<DocActionButton onClick={() => rmHarm(r.id)} title="Odebrat řádek">✕</DocActionButton>}
              />
              <DocRow k="Plánovaný termín" mono value={r.plan} onChange={(v) => updHarm(r.id, { plan: v })} placeholder="30. 05. 2026" />
              <DocRow k="Skutečný stav" value={r.skutecnost} onChange={(v) => updHarm(r.id, { skutecnost: v })} placeholder="Dokončeno / zpoždění X dní…" />
            </Fragment>
          ))}
        </DocBlock>

        {/* 05 — Program */}
        <DocBlock
          idx="05"
          title="Body programu / projednávané věci"
          actions={<DocActionButton onClick={addProg} title="Přidat bod">+ Bod</DocActionButton>}
        >
          {data.program.length === 0 && (
            <DocRow k="—" v={<span style={{ color: 'var(--sd-doc-dim)' }}>Zatím žádné body. Použij „+ Bod".</span>} />
          )}
          {data.program.map((r, i) => (
            <Fragment key={r.id}>
              <DocRow
                k={`${i + 1}. Popis`}
                multiline
                value={r.popis}
                onChange={(v) => updProg(r.id, { popis: v })}
                placeholder="Projednávaný bod, rozhodnutí…"
                actions={<DocActionButton onClick={() => rmProg(r.id)} title="Odebrat bod">✕</DocActionButton>}
              />
              <DocRow k="Odpovědný" value={r.odpovedny} onChange={(v) => updProg(r.id, { odpovedny: v })} placeholder="Jméno / role" />
              <DocRow
                k="Termín"
                mono
                value={r.termin}
                onChange={(v) => updProg(r.id, { termin: v })}
                placeholder="dd. mm. rrrr"
                actions={<DocInlineDate value={r.termin} onChange={(v) => updProg(r.id, { termin: v })} />}
              />
            </Fragment>
          ))}
        </DocBlock>

        {/* 06 — Vady */}
        <DocBlock
          idx="06"
          title="Vady a nedodělky"
          actions={<DocActionButton onClick={addVada} title="Přidat vadu">+ Vada</DocActionButton>}
        >
          {data.vady.length === 0 && (
            <DocRow k="—" v={<span style={{ color: 'var(--sd-doc-dim)' }}>Žádné vady evidované v tomto zápise.</span>} />
          )}
          {data.vady.map((r, i) => (
            <Fragment key={r.id}>
              <DocRow
                k={`Vada ${i + 1}`}
                multiline
                value={r.popis}
                onChange={(v) => updVada(r.id, { popis: v })}
                placeholder="Popis vady / nedodělku"
                actions={<DocActionButton onClick={() => rmVada(r.id)} title="Odebrat vadu">✕</DocActionButton>}
              />
              <DocRow k="Místo" value={r.misto} onChange={(v) => updVada(r.id, { misto: v })} placeholder="1.NP, chodba…" />
              <DocRow
                k="Termín odstranění"
                mono
                value={r.termin}
                onChange={(v) => updVada(r.id, { termin: v })}
                placeholder="dd. mm. rrrr"
                actions={<DocInlineDate value={r.termin} onChange={(v) => updVada(r.id, { termin: v })} />}
              />
            </Fragment>
          ))}
        </DocBlock>

        {/* 07 — Přílohy */}
        <DocBlock idx="07" title="Přílohy">
          <DocRow
            k="Seznam"
            multiline
            value={data.prilohy ?? ''}
            onChange={(v) => patch({ prilohy: v })}
            placeholder="Fotodokumentace, statický posudek, ..."
          />
        </DocBlock>

        {/* 08 — Rozeslání */}
        <DocBlock idx="08" title="Rozeslání">
          <DocRow
            k="Příjemci"
            multiline
            value={data.rozeslani ?? ''}
            onChange={(v) => patch({ rozeslani: v })}
            placeholder="Zápis byl rozeslán: Objednatel, TDS, Zhotovitel"
          />
        </DocBlock>
      </DocPage>
      </div>

      {/* ═══ FOTKY + PŘÍLOHY — netiskne se ═══ */}
      <div className="docNoPrint" style={{ marginTop: 20, display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))' }}>
        {/* Fotky */}
        <div style={{ background: '#fff', border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, padding: '14px 16px', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'var(--sd-yellow)' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4, marginBottom: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--sd-doc-ink)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              📷 Fotky z KD ({kdPhotos.length})
            </div>
            <Button variant="secondary" size="sm" onClick={() => { setPickedPhotoIds(new Set()); setPhotoPickerOpen(true); }}>
              Vybrat z galerie
            </Button>
          </div>
          {kdPhotos.length === 0 ? (
            <div style={{ color: 'var(--sd-doc-dim)', fontSize: 12, padding: '10px 0' }}>
              Žádné fotky nepřiřazeny. Klikni „Vybrat z galerie" a propoj existující fotky stavby s tímto KD.
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {kdPhotos.map((p) => (
                <KdPhotoThumb key={p.id} stavbaId={activeStavba.id} photoId={p.id} label={p.popis || p.filename} />
              ))}
            </div>
          )}
        </div>

        {/* Přílohy (skeny) */}
        <div style={{ background: '#fff', border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, padding: '14px 16px', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'var(--sd-yellow)' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4, marginBottom: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--sd-doc-ink)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              📄 Přílohy / skeny ({attachments.length})
            </div>
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadAtt.isPending}
              >
                {uploadAtt.isPending ? 'Nahrávám…' : 'Nahrát soubor'}
              </Button>
            </>
          </div>
          {attachments.length === 0 ? (
            <div style={{ color: 'var(--sd-doc-dim)', fontSize: 12, padding: '10px 0' }}>
              Žádné přílohy. Nahraj scan zápisu, fotku whiteboardu nebo dokument.
            </div>
          ) : (
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {attachments.map((a) => (
                <li
                  key={a.id}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '6px 8px', background: 'var(--sd-w-faf8ef, #faf8ef)', border: '1px solid var(--sd-w-ece8d8, #ece8d8)', borderRadius: 4 }}
                >
                  <a
                    href={`/api/stavby/${activeStavba.id}/kd/${kdIdNum}/attachments/${a.id}/content`}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: 'var(--sd-doc-ink)', fontSize: 12, fontWeight: 600, textDecoration: 'none', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={a.filename}
                  >
                    {a.filename}
                  </a>
                  <span style={{ fontSize: 11, color: 'var(--sd-doc-dim)', fontFamily: 'var(--sd-font-mono)' }}>
                    {(a.size / 1024).toFixed(1)} kB
                  </span>
                  <button
                    onClick={() => {
                      if (window.confirm(`Smazat přílohu „${a.filename}"?`)) deleteAtt.mutate(a.id);
                    }}
                    style={{ background: 'none', border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, padding: '2px 7px', cursor: 'pointer', color: '#b46060', fontSize: 11 }}
                    title="Smazat"
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Email modal */}
      <SendEmailModal
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        buildHtml={() => buildDocHtml(previewRef.current)}
        docType="zapis-kd"
        docTitle={`Zápis z kontrolního dne ${cisloLabel} — ${activeStavba.nazev}`}
        defaultSubject={`KD ${cisloLabel} — ${activeStavba.nazev}`}
        meta={`Stavba: ${activeStavba.nazev} · ${data.datum || ''}`}
      />

      {/* Photo picker modal */}
      <Modal
        open={photoPickerOpen}
        onClose={() => setPhotoPickerOpen(false)}
        title="Vybrat fotky ze stavby"
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setPhotoPickerOpen(false)}>Zrušit</Button>
            <Button
              variant="primary"
              onClick={handleLinkPickedPhotos}
              disabled={linkPhotos.isPending || pickedPhotoIds.size === 0}
            >
              {linkPhotos.isPending ? 'Propojuji…' : `Přidat (${pickedPhotoIds.size})`}
            </Button>
          </>
        }
      >
        {allPhotos.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--sd-doc-dim)' }}>
            Stavba nemá žádné fotografie. Nejdřív nahraj fotky v sekci Fotodokumentace.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8, maxHeight: 520, overflowY: 'auto' }}>
            {allPhotos.map((p) => {
              const picked = pickedPhotoIds.has(p.id);
              return (
                <label
                  key={p.id}
                  style={{ cursor: 'pointer', border: picked ? '2px solid var(--sd-yellow)' : '2px solid transparent', borderRadius: 4, position: 'relative', aspectRatio: '4/3', overflow: 'hidden', background: 'var(--sd-w-faf8ef, #faf8ef)' }}
                >
                  <input
                    type="checkbox"
                    checked={picked}
                    onChange={() => togglePick(p.id)}
                    style={{ position: 'absolute', top: 4, left: 4, zIndex: 2 }}
                  />
                  <KdPhotoThumb stavbaId={activeStavba.id} photoId={p.id} label={p.popis || p.filename} fill />
                </label>
              );
            })}
          </div>
        )}
      </Modal>

      {/* Šablony modal */}
      <Modal
        open={templatesOpen}
        onClose={() => setTemplatesOpen(false)}
        title="Šablony zápisů z KD"
        size="md"
        footer={<Button variant="secondary" onClick={() => setTemplatesOpen(false)}>Zavřít</Button>}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, padding: 12, background: 'var(--sd-w-faf8ef, #faf8ef)' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--sd-doc-ink)', marginBottom: 8, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              Uložit aktuální obsah jako šablonu
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                value={tplNewName}
                onChange={(e) => setTplNewName(e.target.value)}
                placeholder="Název šablony (např. Týdenní KD)"
                style={{ flex: 1, padding: '6px 10px', border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, background: '#fff', color: 'var(--sd-doc-ink)', fontSize: 13 }}
              />
              <Button
                variant="primary"
                onClick={handleSaveTemplate}
                disabled={createTpl.isPending || !tplNewName.trim()}
              >
                {createTpl.isPending ? 'Ukládám…' : 'Uložit'}
              </Button>
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--sd-doc-ink)', marginBottom: 6, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              Načíst šablonu ({templates.length})
            </div>
            {templates.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', color: 'var(--sd-doc-dim)', fontSize: 13, background: '#fff', border: '1px dashed var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4 }}>
                Zatím žádné uložené šablony. Ulož první pomocí pole výše.
              </div>
            ) : (
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {templates.map((tpl) => (
                  <li
                    key={tpl.id}
                    style={{ padding: '8px 12px', background: '#fff', border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}
                  >
                    <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13, fontWeight: 600 }}>
                      {tpl.name}
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={async () => {
                        // Načíst detail a aplikovat
                        try {
                          const full = await (await fetch(`/api/templates/${tpl.id}`, {
                            headers: getAuthToken() ? { Authorization: `Bearer ${getAuthToken()}` } : undefined,
                          })).json();
                          handleLoadTemplate(full);
                        } catch (e) {
                          window.alert(`Chyba: ${e instanceof Error ? e.message : 'neznámá'}`);
                        }
                      }}
                    >
                      📂 Načíst
                    </Button>
                    <button
                      onClick={() => {
                        if (window.confirm(`Smazat šablonu „${tpl.name}"?`)) deleteTpl.mutate(tpl.id);
                      }}
                      style={{ background: 'none', border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, padding: '4px 8px', cursor: 'pointer', color: '#b46060', fontSize: 11 }}
                      title="Smazat"
                    >
                      ✕
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </Modal>

      {/* Historie KD modal */}
      <Modal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        title="Historie kontrolních dnů"
        size="lg"
        footer={<Button variant="secondary" onClick={() => setHistoryOpen(false)}>Zavřít</Button>}
      >
        {allKd.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--sd-doc-dim)' }}>
            Žádné další KD.
          </div>
        ) : (
          <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {allKd.map((k) => (
              <li
                key={k.id}
                style={{ padding: '10px 12px', background: k.id === kdIdNum ? '#fef3c7' : 'var(--sd-w-faf8ef, #faf8ef)', border: '1px solid var(--sd-w-ece8d8, #ece8d8)', borderRadius: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, cursor: 'pointer' }}
                onClick={() => {
                  setHistoryOpen(false);
                  navigate(`/app/kontrolni-dny/${k.id}`);
                }}
              >
                <div>
                  <div style={{ fontFamily: 'var(--sd-font-mono)', fontWeight: 800, fontSize: 12 }}>
                    KD-{String(k.cislo).padStart(2, '0')}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--sd-doc-dim)' }}>
                    {k.datum ? k.datum.slice(0, 10) : '—'}
                    {k.nesplneno_count > 0 && <> · <span style={{ color: '#b45309', fontWeight: 700 }}>{k.nesplneno_count} nesplněno</span></>}
                  </div>
                </div>
                <div style={{ fontSize: 11, color: k.stav === 'uzavreno' ? 'var(--sd-success)' : 'var(--sd-yellow-text)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  {k.stav === 'uzavreno' ? '✓ uzavřen' : k.stav || 'koncept'}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Modal>
    </DocLayout>
  );
}

/**
 * Mini thumbnail pro fotku KD — stahuje binary přes fetch s JWT tokenem
 * a zobrazí blob URL. (Port z FotodokumentacePage.)
 */
export function KdPhotoThumb({ stavbaId, photoId, label, fill }: { stavbaId: number; photoId: number; label: string; fill?: boolean }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    const token = getAuthToken();
    let blobUrl: string | null = null;
    let cancelled = false;
    fetch(`/api/stavby/${stavbaId}/photos/${photoId}/thumbnail`, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined)
      .then((r) => (r.ok ? r.blob() : Promise.reject(r.status)))
      .then((b) => {
        if (cancelled) return;
        blobUrl = URL.createObjectURL(b);
        setSrc(blobUrl);
      })
      .catch(() => { /* ignore */ });
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [stavbaId, photoId]);
  return (
    <div
      style={{
        width: fill ? '100%' : 96,
        height: fill ? '100%' : 72,
        background: 'var(--sd-w-faf8ef, #faf8ef)',
        border: fill ? 'none' : '1px solid var(--sd-w-d8d4c5, #d8d4c5)',
        borderRadius: fill ? 0 : 3,
        position: 'relative',
        overflow: 'hidden',
      }}
      title={label}
    >
      {src && <img src={src} alt={label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
    </div>
  );
}
